import { z } from 'zod';

/**
 * What the API serialises — the migration runner's contract, relayed — parsed
 * rather than cast. Unknown keys are stripped; the enums are closed, because a
 * state this screen does not know is one it cannot tell finished from running.
 *
 * Source of truth: `openspec/changes/bulk-salesforce-migration/design.md`,
 * "Runner HTTP contract".
 */

export const JOB_STATES = ['queued', 'planning', 'running', 'done', 'failed'] as const;
export const JobState = z.enum(JOB_STATES);
export type JobState = z.infer<typeof JobState>;

export const JobMode = z.enum(['dry-run', 'launch', 'report']);
export type JobMode = z.infer<typeof JobMode>;

export const TENANT_OUTCOMES = [
    'pending',
    'planned',
    'not_migrated',
    'launched',
    'blocked',
    'running',
    'completed',
    'completed_with_warnings',
    'failed',
    'skipped',
    'error',
] as const;
export const TenantOutcome = z.enum(TENANT_OUTCOMES);
export type TenantOutcome = z.infer<typeof TenantOutcome>;

export const ColumnDef = z.object({
    key: z.string(),
    label: z.string(),
    align: z.enum(['left', 'center', 'right']).optional(),
});
export type ColumnDef = z.infer<typeof ColumnDef>;

/**
 * How a module treats records it already migrated when it runs again (INI-0035 D4):
 * `re-evaluate` (a launch re-evaluates the changed ones and keeps what Veevart
 * changed), `update` (a launch can bring Salesforce's changes) or `none` (seed once).
 */
export const RERUN_MODES = ['re-evaluate', 'update', 'none'] as const;
export const RerunMode = z.enum(RERUN_MODES);
export type RerunMode = z.infer<typeof RerunMode>;

export const BulkModule = z.object({
    moduleId: z.string(),
    title: z.string(),
    description: z.string(),
    domain: z.object({ id: z.string(), title: z.string() }),
    order: z.number(),
    columns: z.array(ColumnDef),
    supportsPreview: z.boolean(),
    /** The module may re-sync clients already migrated. Absent (an older API) reads `false`. */
    supportsResync: z.boolean().default(false),
    /** Absent from an older API, and a value this screen does not know reads as absent. */
    rerun: RerunMode.optional().catch(undefined),
    /** `composite` on an entry that runs its `members` in order (INI-0035 D3). */
    kind: z.string().optional(),
    members: z.array(z.string()).optional(),
});
export type BulkModule = z.infer<typeof BulkModule>;

export const JobHeader = z.object({
    moduleId: z.string(),
    reportId: z.string(),
    mode: JobMode,
    state: JobState,
    requestedBy: z.string(),
    reason: z.string().nullable(),
    fromReportId: z.string().nullable(),
    tenantIds: z.array(z.string()).nullable(),
    excludeSandboxes: z.boolean(),
    /** A launch (or a refresh of one) that also re-syncs clients already migrated. */
    resync: z.boolean().default(false),
    /** `'all'` on a launch (or a refresh of one) that re-evaluated every migrated record. */
    rearm: z.literal('all').optional().catch(undefined),
    total: z.number().nullable(),
    done: z.number(),
    counts: z.record(z.number()),
    createdAt: z.string(),
    startedAt: z.string().nullable(),
    finishedAt: z.string().nullable(),
    lastProgressAt: z.string().nullable(),
    error: z.string().nullable(),
    exportLocation: z.string().nullable(),
});
export type JobHeader = z.infer<typeof JobHeader>;

/**
 * One pre-run check (`validate()`) finding on a row: a dry-run's (blocking ones
 * included — they annotate, never change the outcome) or a launch's non-blocking
 * ones. `blocking`: the launch will refuse this client until it is fixed.
 */
export const RowWarning = z.object({
    code: z.string(),
    message: z.string(),
    blocking: z.boolean(),
});
export type RowWarning = z.infer<typeof RowWarning>;

export const RecordRow = z.object({
    /**
     * On a composite's row (one per tenant, the union of its members' records): the
     * member module the record belongs to. Absent on a plain module's record.
     */
    moduleId: z.string().optional(),
    sourceId: z.string(),
    title: z.string(),
    columns: z.record(z.unknown()),
    notMigrated: z.object({ code: z.string(), message: z.string() }).nullable(),
    /**
     * What the analysis expects the record to migrate with (INI-0035 D6: the preview
     * lives here now). Absent when none, and from an older API.
     */
    plannedWarnings: z.array(RowWarning).optional(),
    preview: z.unknown().optional(),
    tracking: z
        .object({
            status: z.string(),
            attempts: z.number(),
            v2Id: z.string().nullable(),
            failure: z.unknown(),
            warnings: z.array(z.unknown()),
            migratedAt: z.string().nullable(),
        })
        .optional(),
});
export type RecordRow = z.infer<typeof RecordRow>;

export const RunView = z.object({
    start: z.enum(['accepted', 'blocked', 'already-running']),
    runId: z.string().nullable(),
    status: z.string().optional(),
    totals: z.record(z.number()).optional(),
    blockedBy: z.unknown().optional(),
    abortedBy: z.unknown().optional(),
    startedAt: z.string().optional(),
    finishedAt: z.string().optional(),
    /** The member module of `runId` on a composite's `already-running` row (INI-0035 D3). */
    moduleId: z.string().optional(),
});
export type RunView = z.infer<typeof RunView>;

export const TenantRow = z.object({
    tenant: z.object({
        tenantId: z.string(),
        slug: z.string().nullable(),
        displayName: z.string(),
        orgId: z.string().nullable(),
        isSandbox: z.boolean(),
        status: z.string(),
        salesforceSystemUser: z.boolean(),
    }),
    outcome: TenantOutcome,
    reason: z.string().nullable(),
    message: z.string().nullable(),
    /** The findings `message` also ends with (`Warnings: …`); `[]` when none. Absent from an older API. */
    warnings: z.array(RowWarning).optional(),
    records: z.array(RecordRow),
    recordsTotal: z.number(),
    /**
     * Dry-run only (INI-0035 follow-ups D1): records not copied by design over the whole
     * discovery, and the same by reason code. Absent when none, and from an older API;
     * a malformed value reads as absent.
     */
    notMigratedByDesign: z.number().int().nonnegative().optional().catch(undefined),
    notMigratedByCode: z.record(z.number().int().nonnegative()).optional().catch(undefined),
    run: RunView.optional(),
    completedAt: z.string().nullable(),
});
export type TenantRow = z.infer<typeof TenantRow>;

export const ModulesResponse = z.object({ modules: z.array(BulkModule) });
export const JobResponse = z.object({ job: JobHeader });
export const JobsResponse = z.object({ jobs: z.array(JobHeader) });
export const JobPage = z.object({
    job: JobHeader,
    rows: z.array(TenantRow),
    nextCursor: z.string().nullable(),
});
export type JobPage = z.infer<typeof JobPage>;
